import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Play,
  Pause,
  X,
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Users,
  PhoneCall,
  PhoneOff,
  RotateCcw,
  Download,
  Moon,
} from "lucide-react";
import {
  isWithinBusinessHours,
  nextWindowOpenAt,
  formatDays,
  formatWindowLocal,
} from "@/lib/business-hours";
import { formatInTimeZone } from "date-fns-tz";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PcaFilters, FilterChip } from "@/components/BatchCall/PcaFilters";
import { SlidersHorizontal } from "lucide-react";
import {
  emptyFilter,
  isFilterActive,
  type FilterMap,
  type PcaField,
} from "@/components/BatchCall/pca-filter-types";
import { toCsv, downloadCsv, type CsvColumn } from "@/lib/csv";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import {
  campaignKeys,
  importOpen,
  useCampaign,
  useCampaignAction,
  useCampaignContactStats,
  useCampaignContacts,
  useRetryImport,
  useUpdateCampaign,
} from "@/services/campaign.query";
import {
  listAllCampaignContacts,
  type AnalysisFilter,
  type Campaign,
  type CampaignAction,
  type CampaignContact,
  type CampaignImportDetail,
  type ContactsQuery,
} from "@/services/campaign.service";
import { useAgent } from "@/services/assistant.query";
import { LOADING, linkedName, linkedRow } from "@/utils/placeholder";
import { isNotFound } from "@/lib/api-client";
import {
  campaignExportFields,
  campaignFilterFields,
  withFoundPcaFields,
} from "@/utils/campaign-analysis";
import { usePhoneNumbers } from "@/hooks/use-phone-numbers";
import { useToast } from "@/hooks/use-toast";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

const STATUS_BADGE: Record<Campaign["status"], "default" | "secondary" | "destructive"> = {
  draft: "secondary",
  scheduled: "default",
  warmup: "default",
  "in-progress": "default",
  paused: "secondary",
  dormant: "secondary",
  completed: "secondary",
  cancelled: "destructive",
};

const count = (n: number) => n.toLocaleString("en-US");

/**
 * Where the campaign's contacts import is: on its way (with how far), failed (with why, and Retry), and the lines
 * the worker skipped with their reasons — the first 100 of them, which is what the import keeps.
 */
function ImportBanner({ file, canRetry, retrying, onRetry }: {
  file: CampaignImportDetail;
  canRetry: boolean;
  retrying: boolean;
  onRetry: () => void;
}) {
  const percent = file.total_lines ? Math.floor((file.lines_read / file.total_lines) * 100) : 0;
  return (
    <div className="mb-6 rounded-lg border p-4 text-sm" role="status" aria-label="Contacts import">
      {file.status === "failed" ? (
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-red-700">The contacts import failed</p>
            <p className="mt-1 text-muted-foreground">{file.error_message}</p>
          </div>
          {canRetry && (
            <Button size="sm" onClick={onRetry} disabled={retrying}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Retry import
            </Button>
          )}
        </div>
      ) : file.status === "done" ? (
        <p className="font-medium">Contacts imported: {count(file.contacts_added)}</p>
      ) : (
        <div className="grid gap-2">
          <p className="font-medium">
            {file.total_lines
              ? `Importing contacts: ${count(file.lines_read)} of ${count(file.total_lines)} lines`
              : "Preparing the contacts import…"}
          </p>
          <Progress value={percent} className="h-2" aria-label="Import progress" />
          <p className="text-xs text-muted-foreground">
            You can leave this page: the import carries on. The campaign can start once it is done.
          </p>
        </div>
      )}
      {file.rows_skipped > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-muted-foreground">
            {count(file.rows_skipped)} line{file.rows_skipped === 1 ? "" : "s"} skipped
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {file.skipped.map((line) => (
              <li key={line.line}>
                Line {line.line}: {line.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default function CampaignDetails() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { currentWorkspaceId } = useWorkspace();
  const { canEdit } = useWorkspacePermissions();
  const navigate = useNavigate();
  
  const PAGE_SIZE = 100;
  const { data: campaign, isLoading: campaignLoading } = useCampaign(campaignId);
  const { data: phoneNumbers, isPending: phoneNumbersPending } = usePhoneNumbers(currentWorkspaceId || undefined);
  const campaignAction = useCampaignAction();
  const updateCampaign = useUpdateCampaign();
  const retryImport = useRetryImport();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FilterMap>({});
  const [durationFilter, setDurationFilter] = useState<{ min: string; max: string }>({
    min: "",
    max: "",
  });

  // Only ever one agent matters on this page — the campaign's. Fetching it by
  // id gets the whole config the analysis columns need (the agent list ships a
  // trimmed one) and saves loading every agent in the workspace to read it.
  const agentRead = useAgent(campaign?.agent_id ?? undefined);
  // null once the agent was deleted (a 404) or the campaign has none; a re-read
  // that failed any other way keeps the agent it had.
  const agent = linkedRow(campaign?.agent_id, agentRead.data, isNotFound(agentRead.error));

  // The analysis keys of every call loaded so far: the filters of a campaign
  // whose agent is gone. They only ever grow, so a filter that matches nothing
  // does not take its own chip away.
  const [foundFields, setFoundFields] = useState<PcaField[]>([]);
  const pcaFields = useMemo(() => campaignFilterFields(agent, foundFields), [agent, foundFields]);

  // Serialize the active duration + PCA filters into the server-side query params. A number box's value is
  // blank or a valid number (the browser sanitizes it), so a non-blank one is never NaN.
  const durationMin = durationFilter.min.trim() === "" ? null : Number(durationFilter.min);
  const durationMax = durationFilter.max.trim() === "" ? null : Number(durationFilter.max);
  const pcaFilterPayload = useMemo<AnalysisFilter[]>(
    () =>
      pcaFields
        .map((f) => ({ field: f, filter: filters[f.name] ?? emptyFilter(f) }))
        .filter(({ filter }) => isFilterActive(filter))
        .map(({ field, filter }) => ({ name: field.name, ...filter })),
    [pcaFields, filters],
  );

  // Debounce the filter inputs that feed the query so typing into a filter box
  // (duration min/max, or a PCA "contains" text box) doesn't fire a contacts RPC
  // on every keystroke — each query can take seconds on a large campaign. The
  // inputs stay bound to the immediate state above, so typing and the ACTIVE
  // badge are instant; only the values driving the React Query key are debounced.
  const debouncedDurationMin = useDebouncedValue(durationMin, 400);
  const debouncedDurationMax = useDebouncedValue(durationMax, 400);
  const debouncedPcaFilterPayload = useDebouncedValue(pcaFilterPayload, 400);

  const queryParams = useMemo<ContactsQuery>(
    () => ({
      page,
      size: PAGE_SIZE,
      durationMin: debouncedDurationMin,
      durationMax: debouncedDurationMax,
      analysisFilters: debouncedPcaFilterPayload,
    }),
    [page, debouncedDurationMin, debouncedDurationMax, debouncedPcaFilterPayload],
  );

  const {
    data: contactsPage,
    isLoading: contactsLoading,
    isFetching: contactsFetching,
    refetch: refetchContacts,
  } = useCampaignContacts(campaignId, queryParams);
  const { data: stats } = useCampaignContactStats(campaignId);

  useEffect(() => {
    setFoundFields((known) =>
      withFoundPcaFields(known, (contactsPage?.rows ?? []).map((c) => c.call_log?.analysis_data)),
    );
  }, [contactsPage]);

  const contacts = contactsPage?.rows ?? [];
  const filteredTotal = contactsPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE));

  // Reset to the first page whenever the (debounced) active filters change, so
  // the page reset lands together with the refetch rather than on each keystroke.
  const filterSignature = JSON.stringify({
    durationMin: debouncedDurationMin,
    durationMax: debouncedDurationMax,
    pcaFilters: debouncedPcaFilterPayload,
  });
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  // Keep the page index within range as the filtered total shrinks.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Each read throws on failure (React Query's would only record it), so a refresh that failed says so.
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchContacts({ throwOnError: true }),
        queryClient.invalidateQueries({ queryKey: campaignKeys.stats(campaignId) }, { throwOnError: true }),
        queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) }, { throwOnError: true }),
      ]);
      toast({ title: "Refreshed", description: "Campaign data updated." });
    } catch (error) {
      toast({ variant: "destructive", title: "Refresh failed", description: (error as Error).message });
    } finally {
      setIsRefreshing(false);
    }
  };

  const phoneNumber = phoneNumbers?.find(p => p.id === campaign?.phone_number_id);

  // The caller ID (outbound number this campaign dials FROM) is read fresh by
  // the orchestrator on every dispatch cycle, so it can be safely changed in
  // any non-running state — mirrors the public API's UPDATABLE_STATUSES.
  const callerIdEditable =
    campaign?.status === 'draft' ||
    campaign?.status === 'scheduled' ||
    campaign?.status === 'paused';
  // Selectable numbers: active workspace numbers (same filter as the create
  // dialog), plus the campaign's current number even if it has since been
  // deactivated, so the dropdown always shows a correct value.
  const activeNumbers = (phoneNumbers ?? []).filter((pn) => pn.is_active);
  const callerIdOptions =
    phoneNumber && !activeNumbers.some((pn) => pn.id === phoneNumber.id)
      ? [phoneNumber, ...activeNumbers]
      : activeNumbers;

  // The contacts and their counts move while a campaign dials, and while its contacts import: both refresh every 5 s,
  // and once more when they stop, so the import's last batch (or the last call) is on screen.
  const contactsMoving = !!campaign && (campaign.status === "in-progress" || importOpen(campaign));
  const wasMoving = useRef(false);
  useEffect(() => {
    const refresh = () => {
      refetchContacts();
      queryClient.invalidateQueries({ queryKey: campaignKeys.stats(campaignId) });
    };
    if (wasMoving.current && !contactsMoving) refresh();
    wasMoving.current = contactsMoving;
    if (!contactsMoving) return;
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [contactsMoving, refetchContacts, queryClient, campaignId]);

  // Drive the business-hours banner — re-evaluate every 60s so the indicator
  // flips at the window edge without a manual refresh.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const businessHoursStatus = useMemo(() => {
    const bh = campaign?.business_hours ?? null;
    // Render the banner for any "outside hours" state the user can see:
    //   - status='in-progress' with hours configured but currently outside the window
    //   - status='dormant' (the campaign has been put to sleep until eligibility)
    if (campaign?.status === 'dormant') {
      // For dormant rows, prefer the stored dormant_until — it's the canonical
      // "next dispatch instant" the backend computed. Falls back to bh-derived.
      const fromBackend = campaign.dormant_until ? new Date(campaign.dormant_until) : null;
      const fromBh = bh ? nextWindowOpenAt(now, bh) : null;
      const next = fromBackend ?? fromBh;
      return {
        cfg: bh,
        nextLocal: next
          ? bh
            ? formatInTimeZone(next, bh.tz, "EEE MMM d, h:mm a zzz")
            : next.toLocaleString()
          : null,
      };
    }
    if (!bh) return null;
    if (campaign?.status !== 'in-progress') return null;
    if (isWithinBusinessHours(now, bh)) return null;
    const next = nextWindowOpenAt(now, bh);
    return {
      cfg: bh,
      nextLocal: next ? formatInTimeZone(next, bh.tz, "EEE MMM d, h:mm a zzz") : null,
    };
  }, [campaign?.business_hours, campaign?.status, campaign?.dormant_until, now]);

  // Seed/prune the filter map as the campaign's PCA fields load or change.
  useEffect(() => {
    setFilters((prev) => {
      const next: FilterMap = {};
      for (const f of pcaFields) {
        next[f.name] = prev[f.name] ?? emptyFilter(f);
      }
      return next;
    });
  }, [pcaFields]);

  const durationActive =
    durationFilter.min.trim() !== "" || durationFilter.max.trim() !== "";

  const anyFilterActive = durationActive || pcaFilterPayload.length > 0;

  const getStatusColor = (status: Campaign["status"]) => STATUS_BADGE[status];

  const getContactStatusIcon = (status: CampaignContact["status"]) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'calling':
        return <PhoneCall className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'retry_scheduled':
        return <RotateCcw className="h-4 w-4 text-orange-500" />;
      case 'cancelled':
        return <PhoneOff className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getContactStatusBadge = (status: CampaignContact["status"]) => {
    const variants: Record<CampaignContact["status"], "default" | "secondary" | "destructive" | "outline"> = {
      pending: "outline",
      calling: "default",
      completed: "secondary",
      failed: "destructive",
      retry_scheduled: "outline",
      cancelled: "secondary",
    };
    const labels: Record<CampaignContact["status"], string> = {
      pending: "Pending",
      calling: "Calling...",
      completed: "Completed",
      failed: "Failed",
      retry_scheduled: "Retry Scheduled",
      cancelled: "Cancelled",
    };
    return (
      <Badge variant={variants[status]}>
        {labels[status]}
      </Badge>
    );
  };

  const handleAction = (action: CampaignAction) => campaignAction.mutate({ id: campaignId!, action });

  if (campaignLoading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-4 w-96 mb-8" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">Campaign not found</h2>
          <p className="text-muted-foreground mb-4">The campaign you're looking for doesn't exist.</p>
          <Button onClick={() => navigate(`/${currentWorkspaceId}/batch-call`)}>
            Back to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  // The picker calls this only for a number other than the one it shows.
  const handleCallerIdChange = (phoneNumberId: string) =>
    updateCampaign.mutate(
      { id: campaign.id, patch: { phone_number_id: phoneNumberId } },
      {
        onSuccess: () =>
          toast({
            title: "Caller ID updated",
            description: "Future calls in this campaign will dial from the new number.",
          }),
      },
    );

  const progressPercent = campaign.total_contacts > 0 
    ? (campaign.completed_calls / campaign.total_contacts) * 100 
    : 0;
  // A draft starts once its contacts are imported; one made before v2 has them already.
  const contactsIn = !campaign.import || campaign.import.status === "done";

  const contactStats = {
    pending: stats?.pending ?? 0,
    calling: stats?.calling ?? 0,
    completed: stats?.completed ?? 0,
    failed: stats?.failed ?? 0,
    retry: stats?.retry_scheduled ?? 0,
    cancelled: stats?.cancelled ?? 0,
  };

  const handleDownloadCsv = async () => {
    setIsExporting(true);
    try {
      // Fetch every matching row (not just the current page) for the export.
      const exportRows = await listAllCampaignContacts(campaign.id, {
        durationMin: queryParams.durationMin,
        durationMax: queryParams.durationMax,
        analysisFilters: queryParams.analysisFilters,
      });

      const coreColumns: CsvColumn<CampaignContact>[] = [
        { key: "phone_number", header: "phone_number", get: (r) => r.phone_number },
        { key: "contact_name", header: "contact_name", get: (r) => r.contact_name },
        { key: "status", header: "status", get: (r) => r.status },
        { key: "attempt_count", header: "attempt_count", get: (r) => r.attempt_count },
        {
          key: "last_attempt_at",
          header: "last_attempt_at",
          get: (r) => r.last_attempt_at,
        },
        {
          key: "disconnection_reason",
          header: "disconnection_reason",
          get: (r) => r.disconnection_reason,
        },
        {
          key: "call_duration_sec",
          header: "call_duration_sec",
          get: (r) => r.call_log?.duration ?? 0,
        },
        {
          key: "call_summary",
          header: "call_summary",
          get: (r) => r.call_log?.summary ?? null,
        },
      ];
      // Every answer the rows hold, whether or not the agent (if any) still asks for it.
      const exportFields = campaignExportFields(
        agent,
        exportRows.map((r) => r.call_log?.analysis_data),
      );
      const pcaColumns: CsvColumn<CampaignContact>[] = exportFields.map((f) => ({
        key: `pca_${f.name}`,
        header: f.name,
        get: (r) => {
          const data = r.call_log?.analysis_data as Record<string, unknown> | null;
          return data ? data[f.name] ?? null : null;
        },
      }));
      const csv = toCsv(exportRows, [...coreColumns, ...pcaColumns]);
      const date = new Date().toISOString().slice(0, 10);
      const safeName = (campaign.campaign_name || "campaign").replace(/[^\w-]+/g, "_");
      downloadCsv(`${safeName}-contacts-${date}.csv`, csv);
    } catch (error) {
      toast({ variant: "destructive", title: "Export failed", description: (error as Error).message });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDisconnectionReason = (reason: string) => {
    return reason
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <ScrollArea className="h-[calc(100vh-4rem)]">
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Back to campaigns"
              onClick={() => navigate(`/${currentWorkspaceId}/batch-call`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold">{campaign.campaign_name}</h1>
                <Badge variant={getStatusColor(campaign.status)}>
                  {campaign.status}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {linkedName(
                  { id: campaign.agent_id, name: agent?.agent_name, pending: agentRead.isPending },
                  LOADING.agent,
                  "Agent",
                )}
                {" • "}
                {linkedName(
                  { id: campaign.phone_number_id, name: phoneNumber?.phone_number, pending: phoneNumbersPending },
                  LOADING.number,
                  "Number",
                )}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {canEdit && campaign.status === 'draft' && (
              <Button
                onClick={() => handleAction('start')}
                disabled={campaignAction.isPending || !contactsIn}
                title={contactsIn ? undefined : "Start the campaign once its contacts are imported"}
              >
                <Play className="w-4 h-4 mr-2" />
                Start Campaign
              </Button>
            )}
            {canEdit && (campaign.status === 'in-progress' || campaign.status === 'warmup' || campaign.status === 'dormant') && (
              <Button
                variant="outline"
                onClick={() => handleAction('pause')}
                disabled={campaignAction.isPending}
              >
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </Button>
            )}
            {canEdit && campaign.status === 'paused' && (
              <Button
                onClick={() => handleAction('resume')}
                disabled={campaignAction.isPending}
              >
                <Play className="w-4 h-4 mr-2" />
                Resume
              </Button>
            )}
            {canEdit && (campaign.status === 'draft' ||
              campaign.status === 'scheduled' ||
              campaign.status === 'warmup' ||
              campaign.status === 'in-progress' ||
              campaign.status === 'paused' ||
              campaign.status === 'dormant') && (
              <Button
                variant="destructive"
                onClick={() => handleAction('cancel')}
                disabled={campaignAction.isPending}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {campaign.import && (campaign.import.status !== "done" || campaign.import.rows_skipped > 0) && (
          <ImportBanner
            file={campaign.import}
            canRetry={canEdit && campaign.status === "draft"}
            retrying={retryImport.isPending}
            onRetry={() => retryImport.mutate(campaign.id)}
          />
        )}

        {/* Business hours banner */}
        {businessHoursStatus && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <Moon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">
                {campaign.status === 'dormant'
                  ? 'Campaign sleeping — dispatch paused'
                  : 'Outside business hours — dispatch paused'}
              </p>
              <p className="mt-1 text-amber-800">
                {businessHoursStatus.cfg && (
                  <>
                    Window: {formatDays(businessHoursStatus.cfg.days)},{" "}
                    {businessHoursStatus.cfg.windows.map(formatWindowLocal).join(", ")} ({businessHoursStatus.cfg.tz}).
                  </>
                )}
                {businessHoursStatus.nextLocal && (
                  <>
                    {businessHoursStatus.cfg ? " " : ""}Resumes <span className="font-medium">{businessHoursStatus.nextLocal}</span>.
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Users className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Contacts</p>
                <p className="text-2xl font-semibold">{campaign.total_contacts}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <PhoneCall className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Calls</p>
                <p className="text-2xl font-semibold">{contactStats.calling}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Successful</p>
                <p className="text-2xl font-semibold">{campaign.successful_calls}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-semibold">{campaign.failed_calls}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <RotateCcw className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Retry</p>
                <p className="text-2xl font-semibold">{contactStats.retry}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Progress */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Campaign Progress</span>
            <span className="text-sm text-muted-foreground">
              {campaign.completed_calls} / {campaign.total_contacts} calls completed
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>{contactStats.pending} pending</span>
            <span>{progressPercent.toFixed(1)}% complete</span>
          </div>
        </Card>

        {/* Campaign Info */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4">
            <h3 className="text-sm font-medium mb-2">Campaign Settings</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Caller ID</span>
                {canEdit && callerIdEditable ? (
                  // A campaign that can still dial has its number: deleting a number is refused while one uses it
                  // (supabase/functions/_shared/app/live-campaigns.ts).
                  <Select
                    value={campaign.phone_number_id!}
                    onValueChange={handleCallerIdChange}
                    disabled={updateCampaign.isPending}
                  >
                    <SelectTrigger className="h-8 w-[190px]" aria-label="Caller ID">
                      <SelectValue placeholder="Select a number" />
                    </SelectTrigger>
                    <SelectContent>
                      {callerIdOptions.map((pn) => (
                        <SelectItem key={pn.id} value={pn.id}>
                          {pn.phone_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span>
                    {linkedName(
                      { id: campaign.phone_number_id, name: phoneNumber?.phone_number, pending: phoneNumbersPending },
                      LOADING.number,
                    )}
                  </span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Retries</span>
                <span>{campaign.max_retries}</span>
              </div>
              {campaign.retry_intervals.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Retry Intervals</span>
                  <span>{campaign.retry_intervals.join(', ')} min</span>
                </div>
              )}
              {campaign.scheduled_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scheduled</span>
                  <span>{format(new Date(campaign.scheduled_at), 'PPp')}</span>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-medium mb-2">Timeline</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{format(new Date(campaign.created_at), 'PPp')}</span>
              </div>
              {campaign.started_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Started</span>
                  <span>{format(new Date(campaign.started_at), 'PPp')}</span>
                </div>
              )}
              {campaign.completed_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completed</span>
                  <span>{format(new Date(campaign.completed_at), 'PPp')}</span>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-medium mb-2">Status Breakdown</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pending</span>
                <span>{contactStats.pending}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Calling</span>
                <span className="text-blue-500">{contactStats.calling}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Completed</span>
                <span className="text-green-500">{contactStats.completed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Failed</span>
                <span className="text-red-500">{contactStats.failed}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Contacts List */}
        <Card className="p-4">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-baseline gap-3">
              <h3 className="text-lg font-semibold tracking-tight">Contacts</h3>
              <span className="text-sm text-muted-foreground">
                {anyFilterActive
                  ? `${filteredTotal} of ${campaign.total_contacts}`
                  : `${filteredTotal} total`}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCsv}
              disabled={isExporting || filteredTotal === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              {isExporting ? "Preparing…" : "Download CSV"}
            </Button>
          </div>

          <div className="mb-5 rounded-lg border bg-muted/30 px-4 py-3.5">
            <div className="mb-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 text-sm font-medium leading-none">
                <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                <span>Filters</span>
                {anyFilterActive && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 h-5 bg-primary/15 px-2 text-[10px] font-semibold uppercase tracking-wider text-primary"
                  >
                    Active
                  </Badge>
                )}
              </div>
              {anyFilterActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDurationFilter({ min: "", max: "" });
                    const next: FilterMap = {};
                    for (const f of pcaFields) next[f.name] = emptyFilter(f);
                    setFilters(next);
                  }}
                  className="h-7 gap-1.5 px-2 text-xs font-medium text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear all
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <FilterChip
                label="Duration (sec)"
                active={durationActive}
                onClear={() => setDurationFilter({ min: "", max: "" })}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="min"
                  value={durationFilter.min}
                  onChange={(e) =>
                    setDurationFilter((d) => ({ ...d, min: e.target.value }))
                  }
                  className="h-7 w-14 border-0 bg-transparent px-0 text-center text-sm leading-none tabular-nums shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
                />
                <span className="text-muted-foreground/70">–</span>
                <Input
                  type="number"
                  min={0}
                  placeholder="max"
                  value={durationFilter.max}
                  onChange={(e) =>
                    setDurationFilter((d) => ({ ...d, max: e.target.value }))
                  }
                  className="h-7 w-14 border-0 bg-transparent px-0 text-center text-sm leading-none tabular-nums shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
                />
              </FilterChip>

              <PcaFilters
                fields={pcaFields}
                filters={filters}
                onChange={setFilters}
              />
            </div>
          </div>

          {contactsLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : contacts.length > 0 ? (
            <div className={`space-y-2 ${contactsFetching ? "opacity-60 transition-opacity" : ""}`}>
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-start justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="mt-0.5">{getContactStatusIcon(contact.status)}</div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {contact.contact_name || contact.phone_number}
                      </p>
                      {contact.contact_name && (
                        <p className="text-sm text-muted-foreground">
                          {contact.phone_number}
                        </p>
                      )}
                      {contact.disconnection_reason && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Reason: <span className="text-foreground">{formatDisconnectionReason(contact.disconnection_reason)}</span>
                        </p>
                      )}
                      {contact.error_message && (
                        <p className="text-xs text-red-600 mt-0.5 truncate" title={contact.error_message}>
                          Error: {contact.error_message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">
                        Attempts: {contact.attempt_count}
                      </p>
                      {contact.last_attempt_at && (
                        <p className="text-xs text-muted-foreground">
                          Last: {format(new Date(contact.last_attempt_at), 'p')}
                        </p>
                      )}
                      {contact.next_retry_at && (
                        <p className="text-xs text-orange-500">
                          Next retry: {format(new Date(contact.next_retry_at), 'p')}
                        </p>
                      )}
                    </div>
                    {getContactStatusBadge(contact.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {anyFilterActive
                ? "No contacts match the active filters"
                : "No contacts in this campaign"}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-between border-t pt-4">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || contactsFetching}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || contactsFetching}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </ScrollArea>
  );
}
